import { type ActionDispatch, createContext, type ReactNode, useContext, useReducer } from 'react';
import type { Notification } from '../../types/notification';
import { tryGetErrorMessage, tryGetTraceId } from '../../utils/error';

type NotificationProviderProps = {
    notifications?: Notification[];
    dispatchNotification?: (() => void) | undefined;
    children: ReactNode;
};

const NotificationsContext = createContext<Notification[]>([]);
const NotificationsDispatchContext = createContext<ActionDispatch<
    [action: NotificationAction]
> | null>(null);

export type NotificationAction =
    | {
          type: 'notifyError';
          payload: {
              error: unknown;
          };
      }
    | {
          type: 'notifySuccess';
          payload: {
              message: string;
          };
      }
    | {
          type: 'addNotification';
          payload: Omit<Notification, 'id'>;
      }
    | {
          type: 'removeNotification';
          payload: {
              id: number;
          };
      }
    | {
          type: 'removeAllNotifications';
      };

const notificationReducer = (
    notifications: Notification[],
    action: NotificationAction,
): Notification[] => {
    const highestId = notifications.reduce(
        (highest, notification) => Math.max(highest, notification.id),
        0,
    );
    const nextId = highestId + 1;

    switch (action.type) {
        case 'notifyError': {
            const message = tryGetErrorMessage(action.payload.error);
            return [
                ...notifications,
                {
                    id: nextId,
                    type: 'error',
                    message,
                    isPersistent: true,
                    traceId: tryGetTraceId(action.payload.error),
                },
            ];
        }
        case 'notifySuccess': {
            return [
                ...notifications,
                {
                    id: nextId,
                    type: 'success',
                    message: action.payload.message,
                    isPersistent: false,
                },
            ];
        }
        case 'addNotification': {
            return [
                ...notifications,
                {
                    ...action.payload,
                    id: nextId,
                },
            ];
        }
        case 'removeNotification': {
            return notifications.filter((notification) => notification.id !== action.payload.id);
        }
        case 'removeAllNotifications': {
            return [];
        }
    }
};

const NotificationsProvider = ({
    notifications: initialNotifications = [],
    dispatchNotification: mockedDispatch,
    children,
}: NotificationProviderProps) => {
    const [notifications, dispatchNotification] = useReducer(
        notificationReducer,
        initialNotifications,
    );

    return (
        <NotificationsContext.Provider value={notifications}>
            <NotificationsDispatchContext.Provider value={mockedDispatch ?? dispatchNotification}>
                {children}
            </NotificationsDispatchContext.Provider>
        </NotificationsContext.Provider>
    );
};

const useNotifications = (): Notification[] => {
    const context = useContext(NotificationsContext);
    if (context === null) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

const useNotificationDispatch = (): ActionDispatch<[action: NotificationAction]> => {
    const context = useContext(NotificationsDispatchContext);
    if (context === null) {
        throw new Error('useNotificationDispatch must be used within a NotificationsProvider');
    }
    return context;
};

export { NotificationsProvider, useNotificationDispatch, useNotifications };
